<template>
  <aside class="wp" data-test="wf-palette" aria-label="Blocks">
    <div class="wp__tabs" role="tablist" aria-label="Kind of block" @keydown.left.prevent="moveTab(-1)" @keydown.right.prevent="moveTab(1)">
      <button
        v-for="t in tabList"
        :key="t.id"
        type="button"
        role="tab"
        class="wp-tab"
        :class="{ 'is-on': t.id === tab }"
        :aria-selected="t.id === tab ? 'true' : 'false'"
        :tabindex="t.id === tab ? 0 : -1"
        :data-test="`wf-tab-${t.id}`"
        @click="$emit('change-tab', t.id)"
      >{{ t.label }}</button>
    </div>

    <div class="wp__body">
      <!-- Settings the builder is still fetching: nothing is offered until it knows which triggers are taken. -->
      <div v-if="busy" class="wp-loading" data-test="wf-palette-loading" aria-hidden="true">
        <span v-for="n in 5" :key="n" class="wp-loading__bar"></span>
      </div>

      <template v-else>
        <p v-if="showIntro" class="wp-intro" data-test="wf-palette-intro">Pick the event in Open Dental that starts this workflow, and drag it onto the canvas.</p>

        <!-- New workflow, trigger placed, no actions yet: point on to the Actions tab. -->
        <div v-if="isTriggers && showLead && placed" class="wp-lead" data-test="wf-lead-card">
          <div class="wp-lead__row">
            <span class="wp-lead__icon"><WfIcon name="checkCircle" :size="18" /></span>
            <div>
              <div class="wp-lead__title">{{ placed.label }} starts this workflow</div>
              <div class="wp-lead__text">Next, add what should happen after it.</div>
            </div>
          </div>
          <div class="wp-lead__cta">
            <button type="button" class="wp-btn" data-test="wf-add-actions" @click="$emit('change-tab', 'actions')">
              Add actions
              <WfIcon name="arrowR" :size="14" :stroke="2.2" />
            </button>
          </div>
        </div>

        <!-- Every trigger already runs a workflow. -->
        <div v-if="showAllUsed" class="wp-all" data-test="wf-all-used">
          <div>
            <div class="wp-all__title">Every trigger you can use already has a workflow</div>
            <div class="wp-all__text">Each trigger runs one workflow. To do more when one of these happens, open its workflow and add an action there.</div>
          </div>
          <div class="wp-all__list">
            <a
              v-for="w in usedWorkflows"
              :key="String(w.WorkflowID)"
              class="wp-wf"
              :href="hrefOf(w.WorkflowID)"
              data-test="wf-used-workflow"
              @click="onWorkflowLink($event, w.WorkflowID)"
            >
              <span class="wp-wf__tile"><WfIcon :name="w.icon" :size="16" /></span>
              <span class="wp-wf__text">
                <span class="wp-wf__name">{{ w.Name }}</span>
                <span class="wp-wf__trigger">{{ w.triggerLabel }}</span>
              </span>
              <span class="wp-wf__chev"><WfIcon name="chevR" :size="16" :stroke="2" /></span>
            </a>
          </div>
        </div>

        <section v-for="g in groups" :key="g.key" class="wp-group" :data-group="g.key">
          <div class="wp-group__head">
            <span class="wp-group__title">{{ g.title }}</span>
            <span v-if="g.count !== ''" class="wp-group__count">{{ g.count }}</span>
          </div>
          <p v-if="g.note" class="wp-group__note">{{ g.note }}</p>

          <div class="wp-group__items" :class="{ 'wp-group__items--tight': g.key === 'soon' }">
            <div
              v-for="it in g.items"
              :key="it.type"
              class="wp-item"
              :class="`wp-item--${it.state}`"
              data-test="wf-palette-item"
              :data-type="it.type"
              :data-state="it.state"
              :draggable="it.state === 'available' ? 'true' : null"
              :role="it.state === 'available' ? 'button' : null"
              :tabindex="it.state === 'available' ? 0 : null"
              :aria-disabled="it.state === 'inert' ? 'true' : null"
              :title="titleOf(it)"
              @dragstart="onDragStart($event, it)"
              @dragend="onDragEnd(it)"
              @click="place(it)"
              @keydown.enter.prevent="place(it)"
              @keydown.space.prevent="place(it)"
            >
              <!-- available: grab it -->
              <template v-if="it.state === 'available'">
                <span class="wp-item__grip"><WfIcon name="grip" :size="14" :stroke="2" /></span>
                <span class="wp-item__tile"><WfIcon :name="it.icon" :size="17" /></span>
                <div class="wp-item__text">
                  <div class="wp-item__label">{{ it.label }}</div>
                  <div v-if="it.description" class="wp-item__desc">{{ it.description }}</div>
                </div>
              </template>

              <!-- inert: would be free, but the canvas already has a trigger (or has none for an action) -->
              <template v-else-if="it.state === 'inert'">
                <span class="wp-item__tile wp-item__tile--quiet"><WfIcon :name="it.icon" :size="17" /></span>
                <span class="wp-item__label wp-item__label--quiet">{{ it.label }}</span>
              </template>

              <!-- the trigger on the canvas -->
              <template v-else-if="it.state === 'current'">
                <span class="wp-item__tile wp-item__tile--solid"><WfIcon :name="it.icon" :size="17" /></span>
                <div class="wp-item__text">
                  <div class="wp-item__label">{{ it.label }}</div>
                  <div class="wp-item__badge"><span class="wp-item__badge-dot"></span>{{ placed && placed.mine ? 'This workflow’s trigger' : 'Starts this workflow' }}</div>
                  <div class="wp-item__line wp-item__line--teal">To use a different trigger, remove this one from the canvas first.</div>
                </div>
              </template>

              <!-- another workflow already runs on it -->
              <template v-else-if="it.state === 'used'">
                <div class="wp-item__top">
                  <span class="wp-item__tile wp-item__tile--quiet"><WfIcon :name="it.icon" :size="17" /></span>
                  <span class="wp-item__label wp-item__label--muted">{{ it.label }}</span>
                  <span class="wp-item__end"><WfIcon name="lock" :size="14" :stroke="2" /></span>
                </div>
                <div class="wp-item__line">Used by <b>{{ it.usedBy ? it.usedBy.Name : '' }}</b></div>
                <button
                  v-if="it.usedBy"
                  type="button"
                  class="wp-link"
                  data-test="wf-add-actions-there"
                  @click.stop="$emit('open-workflow', it.usedBy.WorkflowID)"
                >Add actions there<WfIcon name="arrowR" :size="14" :stroke="2.2" /></button>
              </template>

              <!-- needs a setting first (Mango Voice) -->
              <template v-else-if="it.state === 'setup'">
                <div class="wp-item__top">
                  <span class="wp-item__tile wp-item__tile--amber"><WfIcon :name="it.icon" :size="17" /></span>
                  <span class="wp-item__label wp-item__label--setup">{{ it.label }}</span>
                  <span class="wp-item__end wp-item__end--amber"><WfIcon name="plug" :size="14" :stroke="2" /></span>
                </div>
                <div class="wp-item__line wp-item__line--amber">Connect Mango Voice in Settings to use this trigger.</div>
                <a
                  v-if="settingsHref"
                  class="wp-link"
                  :href="settingsHref"
                  target="_blank"
                  rel="noopener"
                  data-test="wf-open-settings"
                  @click.stop
                >Open Settings<WfIcon name="arrowR" :size="14" :stroke="2.2" /></a>
              </template>

              <!-- coming soon -->
              <template v-else>
                <span class="wp-item__soon-icon"><WfIcon :name="it.icon" :size="17" /></span>
                <span class="wp-item__label wp-item__label--soon">{{ it.label }}</span>
              </template>
            </div>
          </div>
        </section>
      </template>
    </div>
  </aside>
</template>

<script>
/**
 * The builder's left panel (design frames 1c / 1d / 1e / 1f / 1u; Workflow overhaul P3, 2026-10-10): the
 * Triggers and Actions tabs and every state a block can be in. Presentational: the page decides each item's
 * state (src/utils/workflow/triggerStates.js for triggers) and hands over finished items; this draws them.
 *
 * Item states (data-state): available | inert | current | used | setup | soon. ONLY `available` is draggable;
 * it is also placed by a click and by Enter / Space. A drag carries the block type as `text/plain`, and
 * tells the page (drag-start / drag-end) because a browser hides that data while the drag is still over the
 * canvas.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID (mixin-prop-shadow-guardrail).
 */
import WfIcon from './WfIcon.vue';

export default {
  name: 'BuilderPalette',

  components: { WfIcon },

  props: {
    /** 'triggers' | 'actions' */
    tab: { type: String, default: 'triggers' },
    /** [{ type, label, description, icon, state, usedBy }] in the order to draw them. */
    triggers: { type: Array, default: () => [] },
    /** [{ type, label, description, icon, state }] */
    actions: { type: Array, default: () => [] },
    /** { type, label, mine } — the trigger on the canvas, or null. */
    placed: { type: Object, default: null },
    /** Nothing is placed, nothing is free, and at least one trigger is taken. */
    allUsed: { type: Boolean, default: false },
    /** [{ WorkflowID, Name, triggerLabel, icon }] — the workflows behind the taken triggers. */
    usedWorkflows: { type: Array, default: () => [] },
    /** New workflow, trigger placed, no action yet: show the "Next, add what should happen" card. */
    showLead: { type: Boolean, default: false },
    /** The site's workflows are still loading, so it is not known which triggers are taken. */
    busy: { type: Boolean, default: false },
    /** A workflow id -> its builder URL (so a ctrl-click can open it in a new tab). */
    hrefFor: { type: Function, default: null },
    /** Where Mango Voice is set up; '' hides the link. */
    settingsHref: { type: String, default: '' },
  },

  computed: {
    tabList() {
      return [{ id: 'triggers', label: 'Triggers' }, { id: 'actions', label: 'Actions' }];
    },

    isTriggers() {
      return this.tab !== 'actions';
    },

    hasPlaced() {
      return !!this.placed;
    },

    showIntro() {
      return this.isTriggers && !this.hasPlaced && !this.allUsed;
    },

    showAllUsed() {
      return this.isTriggers && this.allUsed;
    },

    groups() {
      return this.isTriggers ? this.triggerGroups() : this.actionGroups();
    },
  },

  watch: {
    // The trigger list is long, so a block is often picked from low down. What changes next (the "starts this
    // workflow / Add actions" card, the "Used by" list, the other tab's list) is at the TOP of the body: bring it
    // back into view instead of leaving it above the fold (found in a real browser, Cypress 2026-10-10).
    hasPlaced() { this.scrollBodyToTop(); },
    tab() { this.scrollBodyToTop(); },
  },

  methods: {
    scrollBodyToTop() {
      this.$nextTick(() => {
        const body = this.$el && this.$el.querySelector ? this.$el.querySelector('.wp__body') : null;
        if (body) body.scrollTop = 0;
      });
    },

    triggerGroups() {
      const by = (...states) => this.triggers.filter(t => states.indexOf(t.state) !== -1);
      const groups = [];

      const current = by('current');
      if (current.length) {
        groups.push({ key: 'current', title: this.placed && this.placed.mine ? 'This workflow' : 'On the canvas', count: '', note: '', items: current });
      }

      // A free trigger goes quiet (inert) once the canvas has one: it stays in this group with the reason.
      const free = by('available', 'inert');
      if (free.length) {
        groups.push({
          key: 'available',
          title: 'Available',
          count: String(free.length),
          note: this.hasPlaced ? 'A workflow has one trigger, so these are off for now.' : '',
          items: free,
        });
      }

      const used = by('used');
      if (used.length && !this.allUsed) {
        groups.push({
          key: 'used',
          title: 'Used by another workflow',
          count: String(used.length),
          note: 'Each trigger runs one workflow. To do more with one of these, add actions to its workflow.',
          items: used,
        });
      }

      const setup = by('setup');
      if (setup.length) groups.push({ key: 'setup', title: 'Needs setup', count: String(setup.length), note: '', items: setup });

      const soon = by('soon');
      if (soon.length) {
        groups.push({
          key: 'soon',
          title: 'Coming soon',
          count: String(soon.length),
          note: this.allUsed ? `${soon.length} more triggers are on the way.` : '',
          items: this.allUsed ? [] : soon,
        });
      }
      return groups;
    },

    actionGroups() {
      const ready = this.actions.filter(a => a.state !== 'soon');
      const soon = this.actions.filter(a => a.state === 'soon');
      const note = this.hasPlaced
        ? `Drag one onto the canvas. It runs when ${lowerFirst(this.placed.label)} happens.`
        : 'Add a trigger first. Actions run after it.';
      const groups = [{ key: 'actions', title: 'Actions', count: String(ready.length), note, items: ready }];
      if (soon.length) groups.push({ key: 'soon', title: 'Coming soon', count: String(soon.length), note: '', items: soon });
      return groups;
    },

    titleOf(it) {
      if (it.state === 'available') return 'Drag onto the canvas';
      if (it.state === 'inert') return this.isTriggers ? 'This workflow already has a trigger' : 'Add a trigger first';
      if (it.state === 'used') return it.usedBy ? `Already used by ${it.usedBy.Name}` : null;
      return null;
    },

    place(it) {
      if (it.state !== 'available') return;
      this.$emit('place', it.type);
    },

    onDragStart(event, it) {
      if (it.state !== 'available') {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        return;
      }
      try {
        event.dataTransfer.setData('text/plain', it.type);
        event.dataTransfer.effectAllowed = 'copy';
      } catch (e) {
        // a browser without dataTransfer: the page still gets drag-start
      }
      this.$emit('drag-start', it.type);
    },

    onDragEnd(it) {
      if (it.state === 'available') this.$emit('drag-end');
    },

    hrefOf(workflowID) {
      return this.hrefFor ? this.hrefFor(workflowID) : '#';
    },

    /** A plain click opens the workflow here; ctrl / cmd / shift / middle click keeps the browser's new tab. */
    onWorkflowLink(event, workflowID) {
      if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0)) return;
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      this.$emit('open-workflow', workflowID);
    },

    moveTab(step) {
      const ids = this.tabList.map(t => t.id);
      const next = ids[(ids.indexOf(this.tab) + step + ids.length) % ids.length];
      this.$emit('change-tab', next);
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector(`[data-test="wf-tab-${next}"]`);
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
  },
};

function lowerFirst(s) {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

.wp {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: $fu-surface;
  border-right: 1px solid $fu-border;
}

// ---- Tabs (underline) ------------------------------------------------------------------------
.wp__tabs {
  display: flex;
  flex: none;
  gap: 22px;
  padding: 4px 20px 0;
  border-bottom: 1px solid $fu-border;
}

.wp-tab {
  @include fu-button-reset;
  @include fu-focus-ring;
  height: 44px;
  margin-bottom: -1px;
  padding: 0 2px;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 700;
  color: $fu-neutral-500;

  &:hover { color: $fu-text; }

  &.is-on {
    color: $fu-text;
    border-bottom-color: $fu-brand;
  }
}

.wp__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
  padding: 16px 16px 28px;
  overflow-y: auto;
}

.wp-loading {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wp-loading__bar {
  @include fu-skeleton(100%, 52px, 12px);
}

.wp-intro {
  margin: 0;
  padding: 0 4px;
  font-size: 13px;
  line-height: 1.5;
  color: $fu-neutral-600;
}

// ---- The "Next, add actions" card ------------------------------------------------------------
.wp-lead {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid $fu-brand-200;
  border-radius: 14px;
  background: $fu-brand-50;
}

.wp-lead__row {
  display: flex;
  gap: 10px;
}

.wp-lead__icon {
  display: flex;
  flex: none;
  margin-top: 1px;
  color: $fu-brand-700;
}

.wp-lead__title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.wp-lead__text {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-brand-900;
}

.wp-lead__cta { padding-left: 28px; }

.wp-btn {
  @include fu-btn(primary, sm);
}

// ---- "Every trigger already has a workflow" --------------------------------------------------
.wp-all {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid $fu-border;
  border-radius: 14px;
  background: $fu-neutral-25;
}

.wp-all__title {
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
}

.wp-all__text {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: $fu-neutral-600;
}

.wp-all__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wp-wf {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-surface;
  color: $fu-text;
  text-decoration: none;

  &:hover { border-color: $fu-brand-300; text-decoration: none; color: $fu-text; }
}

.wp-wf__tile { @include wb-tile(30px, 8px); }

.wp-wf__text {
  flex: 1;
  min-width: 0;
}

.wp-wf__name {
  display: block;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.wp-wf__trigger {
  display: block;
  font-size: 12.5px;
  color: $fu-neutral-500;
}

.wp-wf__chev {
  display: flex;
  color: $fu-neutral-400;
}

// ---- Groups ----------------------------------------------------------------------------------
.wp-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wp-group__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}

.wp-group__title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $fu-neutral-500;
}

.wp-group__count {
  font-size: 11.5px;
  font-weight: 700;
  color: $fu-neutral-400;
}

.wp-group__note {
  margin: 0;
  padding: 0 4px 2px;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-neutral-600;
}

.wp-group__items {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &--tight { gap: 0; }
}

// ---- One block -------------------------------------------------------------------------------
.wp-item {
  min-width: 0;

  &--available {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px 10px 6px;
    border: 1px solid $fu-border;
    border-radius: 12px;
    background: $fu-surface;
    box-shadow: $fu-shadow-xs;
    cursor: grab;
    @include fu-focus-ring;

    &:hover { border-color: $fu-brand-300; box-shadow: $fu-shadow-sm; }
    &:active { cursor: grabbing; }
  }

  &--inert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border: 1px solid $fu-neutral-100;
    border-radius: 12px;
    background: $fu-neutral-25;
    cursor: not-allowed;
  }

  &--current {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    border: 1.5px solid $fu-brand-300;
    border-radius: 12px;
    background: $fu-brand-50;
  }

  &--used {
    padding: 10px 12px 11px;
    border: 1px solid $fu-border;
    border-radius: 12px;
    background: $fu-neutral-50;
  }

  &--setup {
    padding: 10px 12px 11px;
    border: 1px solid #f3dcae;
    border-radius: 12px;
    background: #fffaf0;
  }

  &--soon {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 4px;
    color: $fu-neutral-500;
  }
}

.wp-item__grip {
  display: flex;
  margin-top: 10px;
  color: $fu-neutral-300;
}

.wp-item__tile {
  @include wb-tile(34px, 9px);

  &--quiet {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: $fu-neutral-100;
    color: $fu-neutral-400;
  }

  &--solid {
    background: $fu-brand;
    color: #ffffff;
  }

  &--amber {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: $fu-warning-subtle;
    color: $fu-warning-text;
  }
}

.wp-item__text {
  flex: 1;
  min-width: 0;
  padding-left: 2px;
}

.wp-item__label {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;

  &--quiet {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: $fu-neutral-500;
  }

  &--muted {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: $fu-neutral-600;
  }

  &--setup {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: $fu-neutral-700;
  }

  &--soon {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 500;
  }
}

.wp-item__desc {
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-neutral-500;
}

.wp-item__top {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: not-allowed;
}

.wp-item__end {
  display: flex;
  color: $fu-neutral-400;

  &--amber { color: $fu-warning-text; }
}

.wp-item__line {
  margin: 7px 0 0 40px;
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-neutral-600;

  b { font-weight: 700; color: $fu-text; overflow-wrap: anywhere; }

  &--amber { color: #8a4a0b; }

  &--teal {
    margin: 6px 0 0;
    line-height: 1.45;
    color: $fu-brand-800;
  }
}

.wp-item__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 20px;
  margin-top: 6px;
  padding: 0 8px;
  border-radius: 999px;
  background: $fu-surface;
  border: 1px solid $fu-brand-200;
  font-size: 11.5px;
  font-weight: 700;
  color: $fu-brand-800;
}

.wp-item__badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $fu-brand-500;
}

.wp-item__soon-icon {
  display: flex;
  color: $fu-neutral-300;
}

.wp-link {
  @include fu-link-btn;
  margin: 4px 0 0 40px;
  font-size: 13px;
  font-weight: 700;
  color: $fu-link;
  text-decoration: none;

  &:hover { text-decoration: underline; color: $fu-link; }
}
</style>
